/**
 * Vertical-line move notation, in both of its spellings:
 * Chinese (中文縱線格式) 炮二平五, and WXF C2=5.
 *
 * They are the same system — same file numbering, same three directions, same
 * step-count-versus-destination rule — differing only in the glyphs, so they
 * share one implementation here and differ only at the final render step.
 *
 * The rules encoded here (from xqbase.com/protocol/cchess_move.htm):
 *
 *  1. File numbering is from each player's OWN right edge.
 *     - Red files are 一..九 counted right→left from Red's side.
 *     - Black files are 1..9 counted right→left from Black's side.
 *     Because the players sit opposite, Red file 一 and Black file 1 are on
 *     OPPOSITE edges of the board. This mirror is the classic parsing bug.
 *
 *  2. Move shape: piece + file + direction(進/退/平) + target.
 *     - 進 advance, 退 retreat, 平 traverse.
 *     - For diagonal movers (馬/相/仕) the final token is the DESTINATION file.
 *     - For straight movers (車/炮/兵/將) moving vertically, the final token is
 *       the NUMBER OF STEPS; moving horizontally, it is the destination file.
 *
 *  3. Stacked identical pieces on one file use 前/後 (front/back) instead of the
 *     origin file number. Special cases:
 *       - Advisors (仕) and elephants (相) never need 前/後: two of them on one
 *         file are always two ranks apart, so one reaches a shared destination
 *         by 進 and the other by 退, and the direction disambiguates.
 *       - Pawns: 3 on a file -> 前/中/後; >3 -> 一二三四五 front-to-back; pawns
 *         stacked across two files -> numbered right-to-left then front-to-back.
 *
 * Rendering uses the same characters the board draws (Red 帥仕相傌俥炮兵, Black
 * 將士象馬車砲卒) so the notation matches the pieces the reader is looking at.
 * Parsing is deliberately more forgiving than rendering and accepts the common
 * variants — 马/車/进/后 and so on — because those are what people type.
 *
 * `chineseToMove` resolves against `legalMoves`, so it can never return a move
 * that is not legal in the given position, and returns null rather than
 * guessing when a string is ambiguous.
 */

import { legalMoves } from "./movegen.js";
import type { Position } from "./position.js";
import {
  Color,
  FILES,
  PieceType,
  fileOf,
  forward,
  rankOf,
  type Move,
  type Square,
} from "./types.js";

const RED_DIGITS = ["一", "二", "三", "四", "五", "六", "七", "八", "九"] as const;
const BLACK_DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const;

/** The piece characters the board draws, indexed by PieceType then Color. */
const PIECE_CHARS: Readonly<Record<PieceType, readonly [string, string]>> = {
  [PieceType.King]: ["帥", "將"],
  [PieceType.Advisor]: ["仕", "士"],
  [PieceType.Elephant]: ["相", "象"],
  [PieceType.Horse]: ["傌", "馬"],
  [PieceType.Rook]: ["俥", "車"],
  [PieceType.Cannon]: ["炮", "砲"],
  [PieceType.Pawn]: ["兵", "卒"],
};

/**
 * Every character that names a piece, including the variants we do not emit.
 * Colour is never read from the character — it comes from the side to move —
 * so accepting both halves of a pair here is forgiving rather than ambiguous.
 */
const PIECE_BY_CHAR: ReadonlyMap<string, PieceType> = new Map([
  ["帥", PieceType.King], ["將", PieceType.King], ["帅", PieceType.King], ["将", PieceType.King],
  ["仕", PieceType.Advisor], ["士", PieceType.Advisor],
  ["相", PieceType.Elephant], ["象", PieceType.Elephant],
  ["傌", PieceType.Horse], ["馬", PieceType.Horse], ["马", PieceType.Horse],
  ["俥", PieceType.Rook], ["車", PieceType.Rook], ["车", PieceType.Rook],
  ["炮", PieceType.Cannon], ["砲", PieceType.Cannon], ["包", PieceType.Cannon],
  ["兵", PieceType.Pawn], ["卒", PieceType.Pawn],
]);

const ADVANCE = new Set(["進", "进"]);
const RETREAT = new Set(["退"]);
const TRAVERSE = new Set(["平"]);

const FRONT = new Set(["前"]);
const MIDDLE = new Set(["中"]);
const BACK = new Set(["後", "后"]);

/** Pieces whose final token is always the destination file, never a step count. */
const DIAGONAL = new Set([PieceType.Advisor, PieceType.Elephant, PieceType.Horse]);

/**
 * Convert an internal file index (0 = 'a', the left edge from Red's view) to the
 * 1-based file NUMBER as that color counts it (from its own right edge).
 *
 * Red counts right→left from Red's right (file 'i'/8), so Red-number = 9 - file.
 * Black counts right→left from Black's right (file 'a'/0), so Black-number = file + 1.
 */
export function fileNumber(fileIndex: number, color: Color): number {
  return color === Color.Red ? FILES - fileIndex : fileIndex + 1;
}

/** The inverse of `fileNumber`: that color's 1-based file number to a file index. */
export function fileIndexFromNumber(n: number, color: Color): number {
  return color === Color.Red ? FILES - n : n - 1;
}

/** Render a file number as the character that color uses (一..九 for Red, 1..9 for Black). */
export function fileGlyph(fileIndex: number, color: Color): string {
  const n = fileNumber(fileIndex, color);
  return color === Color.Red ? RED_DIGITS[n - 1]! : BLACK_DIGITS[n - 1]!;
}

/** Convenience for tests / callers that already hold a square. */
export function fileGlyphOfSquare(sq: Square, color: Color): string {
  return fileGlyph(fileOf(sq), color);
}

/** Render 1..9 in the digits that color writes with. */
function digit(n: number, color: Color): string {
  return color === Color.Red ? RED_DIGITS[n - 1]! : BLACK_DIGITS[n - 1]!;
}

/** Read a digit character as 1..9 for either color, or 0 if it is not one. */
function digitValue(ch: string): number {
  const r = RED_DIGITS.indexOf(ch as (typeof RED_DIGITS)[number]);
  if (r >= 0) return r + 1;
  const b = BLACK_DIGITS.indexOf(ch as (typeof BLACK_DIGITS)[number]);
  return b >= 0 ? b + 1 : 0;
}

/** Squares of every piece of this color and type. */
function piecesOf(pos: Position, color: Color, type: PieceType): Square[] {
  const out: Square[] = [];
  for (let sq = 0; sq < pos.board.length; sq++) {
    const p = pos.board[sq];
    if (p && p.color === color && p.type === type) out.push(sq);
  }
  return out;
}

/**
 * Sort squares so index 0 is the one nearest the opponent.
 *
 * "Front" is a claim about the board, not about rank order: Red advances up the
 * ranks and Black down them, so the comparison has to flip with the colour.
 */
function frontToBack(squares: Square[], color: Color): Square[] {
  const dir = forward(color);
  return [...squares].sort((a, b) => (rankOf(b) - rankOf(a)) * dir);
}

/**
 * The ordered group a stacked piece belongs to, or null if it is alone on its
 * file and can simply be named by that file.
 *
 * Pawns are the awkward case: when two files are both stacked, the convention
 * numbers every stacked pawn in one sequence — right-to-left by file from that
 * player's view, then front-to-back within each file — rather than numbering
 * each file separately.
 */
function stackFor(pos: Position, color: Color, type: PieceType, from: Square): Square[] | null {
  const all = piecesOf(pos, color, type);
  const sameFile = all.filter((sq) => fileOf(sq) === fileOf(from));
  if (sameFile.length < 2) return null;

  if (type !== PieceType.Pawn) return frontToBack(sameFile, color);

  const stackedFiles = [...new Set(all.map(fileOf))].filter(
    (f) => all.filter((sq) => fileOf(sq) === f).length >= 2,
  );
  if (stackedFiles.length < 2) return frontToBack(sameFile, color);

  // Right-to-left from this player's view is ascending file NUMBER for them.
  return stackedFiles
    .sort((a, b) => fileNumber(a, color) - fileNumber(b, color))
    .flatMap((f) => frontToBack(all.filter((sq) => fileOf(sq) === f), color));
}

/** 前 / 中 / 後, or a digit, for position `i` in a group of `n`. */
function positionMarker(i: number, n: number, color: Color): string {
  if (n === 2) return i === 0 ? "前" : "後";
  if (n === 3) return ["前", "中", "後"][i]!;
  return digit(i + 1, color);
}

/**
 * Render a move in Chinese notation.
 *
 * The move is described, not validated — callers that need to know a move is
 * legal should ask `isLegalMove`. Round-tripping through `chineseToMove` is the
 * check that the description is *unambiguous*, which is a different property
 * and the one that actually matters for published notation.
 */
interface Described {
  readonly color: Color;
  readonly type: PieceType;
  /** Where in a stack of identical pieces this one sits, or null if it is alone. */
  readonly stackIndex: number | null;
  readonly stackSize: number;
  /** That colour's 1-based number for the file the piece starts on. */
  readonly fromFileNumber: number;
  readonly direction: "advance" | "retreat" | "traverse";
  /** True when the final token names a destination file rather than a step count. */
  readonly targetIsFile: boolean;
  readonly targetValue: number;
}

/** Everything both spellings need, worked out once. */
function describe(pos: Position, move: Move): Described {
  const piece = pos.pieceAt(move.from);
  if (!piece) throw new Error(`no piece on square ${move.from} to describe`);
  const { color, type } = piece;

  // Advisors and elephants are never marked 前/後: see the module header.
  const stack = type === PieceType.Advisor || type === PieceType.Elephant
    ? null
    : stackFor(pos, color, type, move.from);

  const rankDelta = (rankOf(move.to) - rankOf(move.from)) * forward(color);
  const direction = rankDelta === 0 ? "traverse" : rankDelta > 0 ? "advance" : "retreat";
  const targetIsFile = direction === "traverse" || DIAGONAL.has(type);

  return {
    color,
    type,
    stackIndex: stack ? stack.indexOf(move.from) : null,
    stackSize: stack ? stack.length : 0,
    fromFileNumber: fileNumber(fileOf(move.from), color),
    direction,
    targetIsFile,
    targetValue: targetIsFile
      ? fileNumber(fileOf(move.to), color)
      : Math.abs(rankOf(move.to) - rankOf(move.from)),
  };
}

/**
 * Simplified forms, for zh-Hans prose.
 *
 * Physical boards in mainland China still carry the classical glyphs, which is
 * why those are what we RENDER on the board — but notation written into
 * simplified text is written 马8进7, not 馬8進7, and mixing the two inside one
 * paragraph reads as carelessness to anyone who actually reads Chinese.
 *
 * Note that Red and Black collapse onto the same character for the horse and
 * chariot here (马, 车). That loses nothing: the colour is carried by the digit
 * set — 一二三 for Red, 123 for Black — not by the piece name.
 */
const SIMPLIFIED: Readonly<Record<string, string>> = {
  帥: "帅", 將: "将", 傌: "马", 馬: "马", 俥: "车", 車: "车", 砲: "炮",
  進: "进", 退: "退", 平: "平", 後: "后", 前: "前", 中: "中",
};

const toSimplified = (s: string) => [...s].map((c) => SIMPLIFIED[c] ?? c).join("");

export interface ChineseOptions {
  /** Render in simplified characters, for zh-Hans text. Default: traditional. */
  readonly simplified?: boolean;
}

export function moveToChinese(pos: Position, move: Move, opts: ChineseOptions = {}): string {
  const d = describe(pos, move);
  const name = PIECE_CHARS[d.type][d.color === Color.Red ? 0 : 1];

  // 前馬 puts the marker first; 馬八 puts the file second. Same two characters,
  // opposite order, and getting it backwards is unreadable rather than wrong.
  const head = d.stackIndex === null
    ? name + digit(d.fromFileNumber, d.color)
    : positionMarker(d.stackIndex, d.stackSize, d.color) + name;

  const dir = d.direction === "traverse" ? "平" : d.direction === "advance" ? "進" : "退";
  const out = head + dir + digit(d.targetValue, d.color);
  return opts.simplified ? toSimplified(out) : out;
}

/** WXF letters. Both colours use the same letter and the same Arabic digits. */
const WXF_LETTERS: Readonly<Record<PieceType, string>> = {
  [PieceType.King]: "K",
  [PieceType.Advisor]: "A",
  [PieceType.Elephant]: "E",
  [PieceType.Horse]: "H",
  [PieceType.Rook]: "R",
  [PieceType.Cannon]: "C",
  [PieceType.Pawn]: "P",
};

/**
 * The same move in WXF: C2=5, H8+7, +R-1.
 *
 * Unlike the Chinese spelling, WXF writes both sides with Arabic digits, so the
 * colour shows only in which edge the file numbers are counted from — which is
 * exactly the thing that confuses people reading it for the first time.
 */
export function moveToWxf(pos: Position, move: Move): string {
  const d = describe(pos, move);
  const letter = WXF_LETTERS[d.type];
  const head = d.stackIndex === null
    ? letter + d.fromFileNumber
    : (d.stackIndex === 0 ? "+" : d.stackIndex === d.stackSize - 1 ? "-" : String(d.stackIndex + 1)) + letter;
  const op = d.direction === "traverse" ? "=" : d.direction === "advance" ? "+" : "-";
  return head + op + d.targetValue;
}

/**
 * Parse Chinese notation against a position.
 *
 * Returns null rather than throwing for anything it cannot resolve to exactly
 * one legal move — malformed text, a piece that is not there, or a string that
 * genuinely describes two different legal moves. Candidates come from
 * `legalMoves`, so an illegal move can never come back out.
 */
export function chineseToMove(pos: Position, text: string): Move | null {
  const s = [...text.replace(/\s+/g, "")];
  if (s.length !== 4) return null;

  const color = pos.sideToMove;
  const [a, b, dirChar, targetChar] = s as [string, string, string, string];

  const direction = ADVANCE.has(dirChar) ? "進" : RETREAT.has(dirChar) ? "退" : TRAVERSE.has(dirChar) ? "平" : null;
  if (!direction) return null;

  let type: PieceType | undefined;
  let fileIdx: number | null = null;
  let marker: string | null = null;

  if (PIECE_BY_CHAR.has(a)) {
    type = PIECE_BY_CHAR.get(a)!;
    const n = digitValue(b);
    if (!n) return null;
    fileIdx = fileIndexFromNumber(n, color);
  } else {
    type = PIECE_BY_CHAR.get(b);
    if (type === undefined) return null;
    marker = a;
  }

  let candidates = piecesOf(pos, color, type);
  if (fileIdx !== null) {
    candidates = candidates.filter((sq) => fileOf(sq) === fileIdx);
  } else if (marker !== null) {
    // A marker names a position within a stack, so the stack has to be rebuilt
    // from some member of it; any stacked piece yields the same ordered group.
    const stacked = candidates.find((sq) => stackFor(pos, color, type!, sq) !== null);
    if (stacked === undefined) return null;
    const group = stackFor(pos, color, type, stacked)!;
    let idx: number;
    if (FRONT.has(marker)) idx = 0;
    else if (BACK.has(marker)) idx = group.length - 1;
    else if (MIDDLE.has(marker)) idx = group.length === 3 ? 1 : -1;
    else idx = digitValue(marker) - 1;
    if (idx < 0 || idx >= group.length) return null;
    candidates = [group[idx]!];
  }
  if (candidates.length === 0) return null;

  const targetValue = digitValue(targetChar);
  if (!targetValue) return null;

  const matches = legalMoves(pos, color).filter((m) => {
    if (!candidates.includes(m.from)) return false;
    const p = pos.pieceAt(m.from);
    if (!p || p.type !== type) return false;

    const delta = (rankOf(m.to) - rankOf(m.from)) * forward(color);
    const d = delta === 0 ? "平" : delta > 0 ? "進" : "退";
    if (d !== direction) return false;

    return direction === "平" || DIAGONAL.has(type!)
      ? fileNumber(fileOf(m.to), color) === targetValue
      : Math.abs(rankOf(m.to) - rankOf(m.from)) === targetValue;
  });

  return matches.length === 1 ? matches[0]! : null;
}
